import { useState, useMemo, useRef } from 'react';
import { useCurrentEditor } from '@tiptap/react';
import { styled } from '@mui/material/styles';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Button from '@components/Button';
import TextEditor from '@components/TextEditor';
import { Typography } from '@components/Typography';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { handleApiError } from '@helpers/api';
import { useTranslation } from '@hooks';
import { inboxService } from '@services';
import { INTERNAL_NOTE_COLORS } from './internalNoteColors';

// A note never reaches the customer, so this composer deliberately omits channel controls and typing status.
const StyledNoteEditor = styled(TextEditor)({
    paddingBottom: '0 !important',
    '& .ProseMirror': {
        maxHeight: '160px !important',
        minHeight: 'auto !important',
    },
    '& .editor-container': {
        minHeight: '78.5px !important',
        paddingBottom: '46px !important',
        backgroundColor: INTERNAL_NOTE_COLORS.background,
        borderColor: `${INTERNAL_NOTE_COLORS.border} !important`,
        '&:focus-within': {
            padding: '15.5px 13px 45px 13px !important',
            borderColor: `${INTERNAL_NOTE_COLORS.label} !important`,
        },
    },
    '& .editor-toolbar': {
        bottom: '16.5px !important',
    },
});

const StyledButton = styled(Button)({
    height: 24,
    minWidth: 'auto',
    '& .MuiButton-endIcon': {
        margin: 0,
    },
});

// Matches the server-side cap in AddConversationNoteCommand.
const NOTE_MAX_LENGTH = 2000;

const SendNoteButton = ({ disabled, onSend }) => {
    const { editor } = useCurrentEditor();
    return (
        <StyledButton
            variant="contained"
            disabled={disabled}
            onClick={() => onSend(editor)}
            endIcon={<SendRoundedIcon />}
        />
    );
};

const NoteInput = ({ activeConversation }) => {
    const [note, setNote] = useState('');
    const [isSending, setIsSending] = useState(false);

    const containerRef = useRef(null);
    const { t } = useTranslation();

    const isDisabled = !activeConversation || isSending;

    // Editor state is truthy for whitespace-only docs; gate on trimmed text to match the server's empty-content reject.
    const plainNote = useMemo(() => (note ? (parseTipTapJSONToPlainText(note) || '').trim() : ''), [note]);
    const isOverLimit = plainNote.length > NOTE_MAX_LENGTH;
    const canSend = !isDisabled && !!plainNote && !isOverLimit;

    const sendNote = async editor => {
        if (!canSend) {
            return;
        }
        try {
            setIsSending(true);
            await inboxService.sendNote(activeConversation?.id, plainNote);
            setNote('');
            editor?.commands.clearContent(false);
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsSending(false);
        }
    };

    return (
        <StyledNoteEditor
            containerRef={containerRef}
            initialValue={''}
            onChange={setNote}
            editable={!isDisabled}
            containerProps={{
                onKeyDown: async (e, editor) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        await sendNote(editor);
                    }
                },
            }}
            placeholder={t('InboxPage.notePlaceholder')}
            toolbar={
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%',
                        alignItems: 'flex-end',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 14, color: INTERNAL_NOTE_COLORS.label }} />
                        <Typography variant="caption" sx={{ color: INTERNAL_NOTE_COLORS.label }}>
                            {t('InboxPage.noteVisibilityHint')}
                        </Typography>
                        {isOverLimit && (
                            <Typography
                                variant="caption"
                                sx={{ color: 'var(--error-main, #B00020)', marginLeft: '8px' }}
                            >
                                {plainNote.length}/{NOTE_MAX_LENGTH}
                            </Typography>
                        )}
                    </div>
                    <SendNoteButton disabled={!canSend} onSend={sendNote} />
                </div>
            }
        />
    );
};

export default NoteInput;
